import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getMe } from '@/api/auth'
import { AuthContext } from '@/auth/auth-context'
import { can } from '@/auth/permissions'
import type { Permission } from '@/auth/permissions'
import type { AuthContextValue, AuthStatus } from '@/auth/auth-context'
import { authStore } from '@/auth/authStore'
import { onAuthChanged, signIn, signOut } from '@/auth/firebase'
import { hasRole } from '@/auth/roles'
import { FullPageLoader } from '@/components/ui/Spinner'
import type { AdminRole, AdminUser, LoginCredentials } from '@/types/admin'

/**
 * Owns the session lifecycle.
 *
 * Two systems have to agree before anyone is "signed in": Firebase says who they are,
 * and the API says whether they are staff here. Firebase alone is not enough — anyone
 * who can create an account in the project can authenticate, so a Firebase user with no
 * admin_users row is treated exactly as signed out.
 *
 * Renders a loader until that first question has an answer, so no screen flashes
 * signed-out content and then swaps to signed-in.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const state = useSyncExternalStore(authStore.subscribe, authStore.getSnapshot)
  const [bootstrapped, setBootstrapped] = useState(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    // Fires on sign-in, sign-out and every silent token refresh — roughly hourly. Each
    // one re-asks the API who this person is, so a role change or a deactivation takes
    // effect without the user reloading.
    const unsubscribe = onAuthChanged(async (firebaseUser) => {
      if (!firebaseUser) {
        authStore.clear()
        setBootstrapped(true)
        return
      }

      try {
        // redirectOnAuthFailure: false — during bootstrap a 401 means "not staff", which
        // this provider handles by staying anonymous. Letting the api client redirect
        // would fight the router before it has even mounted.
        const user = await getMe({ redirectOnAuthFailure: false })
        authStore.setUser(user)
      } catch {
        // Authenticated with Firebase but not authorised here. Sign out of Firebase too,
        // otherwise the SDK keeps renewing a session this app will never accept and the
        // login screen looks broken rather than refused.
        authStore.clear()
        await signOut().catch(() => {
          // Best effort; the local session is already cleared.
        })
      } finally {
        setBootstrapped(true)
      }
    })

    return unsubscribe
  }, [])

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<AdminUser> => {
      await signIn(credentials.email, credentials.password)

      // Drop anything cached for a previous user before the new session can read it.
      queryClient.clear()

      // Fetched here rather than left to the onIdTokenChanged handler so that a failure
      // surfaces on the login form, where the person can read it. The listener will run
      // too and set the same value.
      const user = await getMe({ redirectOnAuthFailure: false })
      authStore.setUser(user)
      return user
    },
    [queryClient],
  )

  const logout = useCallback(async (): Promise<void> => {
    // Clear locally first: a failed network call must not strand the user signed in.
    authStore.clear()
    queryClient.clear()
    await signOut().catch(() => {
      // Best effort — the local session is already gone.
    })
  }, [queryClient])

  const refreshUser = useCallback(async (): Promise<void> => {
    const user = await getMe()
    authStore.setUser(user)
  }, [])

  const status: AuthStatus = !bootstrapped ? 'loading' : state.user ? 'authenticated' : 'anonymous'

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: state.user,
      isAuthenticated: status === 'authenticated',
      can: (permission: Permission) => can(state.user, permission),
      login,
      logout,
      refreshUser,
      hasRole: (roles: readonly AdminRole[]) => hasRole(state.user, roles),
    }),
    [status, state.user, login, logout, refreshUser],
  )

  if (!bootstrapped) {
    return <FullPageLoader label="Checking your session" />
  }

  return <AuthContext value={value}>{children}</AuthContext>
}
